import type { SocketEvent, SocketNamespace } from '@trinity/chat';
import type { ManagerOptions, SocketOptions } from 'socket.io-client';

type LoggerOptions = {
  isDevEnv: boolean;
  namespace: SocketNamespace;
};

type Logger = (data: LoggerOptions) => (event: SocketEvent, data?: unknown) => void;

const logEvent: Logger = namespace => (event, data) => {
  const types = {
    connect: logInfo,
    disconnect: logWarn,
    authenticated: logInfo,
    unauthorized: logWarn,
  } as Record<string, Logger>;

  const logger: Logger = types[event] || logInfo;
  logger(namespace)(event, data);
};

const logInfo: Logger = l => (event, data) => {
  if (!l.isDevEnv) return;

  console.groupCollapsed(
    `%c socket:${l.namespace} %c${event.toUpperCase()} %c⌁`,
    'color: #979797;',
    'color: #aaffff; font-weight: bold;',
    'color: #d7ff00;',
  );
  if (data) console.info(data);
  console.groupEnd();
};

const logWarn: Logger = l => (event, data) => {
  if (!l.isDevEnv) return;

  console.groupCollapsed(
    `%c socket:${l.namespace} %c${event.toUpperCase()} %c⌁`,
    'color: #979797;',
    'color: #aaffff; font-weight: bold;',
    'color: #c10d34',
  );
  if (data) console.info(data);
  console.groupEnd();
};

const logGeneric = (l: LoggerOptions) => (value: string) => {
  console.log(
    `%c socket:${l.namespace} %c${value} %c⌁`,
    'color: #979797;',
    'color: #aaffff; font-weight: bold;',
    'color: #c10d34',
  );
};

export const createLogger = (opts: LoggerOptions) => ({
  event: logEvent(opts),
  log: logGeneric(opts),
  info: logInfo(opts),
  warn: logWarn(opts),
});

export function resolveSocketOptions(opts: ResolveSocketOptionsParams): Partial<ManagerOptions & SocketOptions> {
  const options: Partial<ManagerOptions & SocketOptions> = {
    path: opts.socketPath,
    withCredentials: true,
    transports: ['websocket', 'polling'],
    ...opts,
  };

  // Include session ID in auth if available
  if (opts.sessionId) {
    options.auth = {
      ...(options.auth || {}),
      ['X-SID']: opts.sessionId,
    };
  }

  return options;
}

export type ResolveSocketOptionsParams = {
  socketPath: string;
  isDevEnv: boolean;
  sessionId?: string;
} & Partial<ManagerOptions & SocketOptions>;